import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import { FeaturePageStructuredData } from "../../config/StructuredData";

const rows = [
  { feature: "Work status tracking", whatsapp: "Scroll through chat history", simplifyte: "Status field on every work item, always visible" },
  { feature: "Task assignment", whatsapp: "Message to a person, easily missed", simplifyte: "Assigned owner with system notification" },
  { feature: "Deadline visibility", whatsapp: "Reminder message that gets buried", simplifyte: "Deadline field with automatic reminders" },
  { feature: "Document requests", whatsapp: "Individual messages per document", simplifyte: "Document checklist per work item" },
  { feature: "Review and approval", whatsapp: "'Checked' or 'OK' in a message", simplifyte: "Structured review step with logged comment" },
  { feature: "Audit trail", whatsapp: "Chat history (deletable, no structure)", simplifyte: "Immutable log on every work item" },
  { feature: "Partner oversight", whatsapp: "Partner must read every message", simplifyte: "Dashboard view of all open work" },
  { feature: "Client communication", whatsapp: "Personal numbers, informal tone", simplifyte: "Structured reminders with professional format" },
  { feature: "Searchability", whatsapp: "Keyword search in chat — unreliable", simplifyte: "Filter by client, period, stage, assignee" },
  { feature: "Closure record", whatsapp: "A message saying 'done'", simplifyte: "Formal closure step with attachment and sign-off" },
];

const WhatsAppVsSimplifyte = () => (
  <>
    <FeaturePageStructuredData
      name="WhatsApp vs Simplifyte for CA Firms and Professional Teams"
      description="Compare WhatsApp vs Simplifyte for managing client work in CA firms. See why professional service teams replace WhatsApp groups with structured workflow management."
      url="https://www.simplifyte.com/compare/whatsapp-vs-simplifyte"
    />
    <HeaderMenu />
    <main style={{ paddingTop: "80px", minHeight: "100vh" }}>
      <section style={{ background: "linear-gradient(135deg,#093A5B 0%,#0a4d6e 100%)", padding: "60px 24px", color: "#fff", textAlign: "center" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <p style={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, marginBottom: "16px" }}>Compare</p>
          <h1 style={{ fontSize: "clamp(26px,4vw,44px)", fontWeight: 700, lineHeight: 1.2, marginBottom: "20px" }}>WhatsApp vs Simplifyte for CA Firms</h1>
          <p style={{ fontSize: "17px", opacity: 0.85, maxWidth: "640px", margin: "0 auto 32px" }}>WhatsApp is for messaging, not workflow. See why CA firms replace WhatsApp groups and threads with Simplifyte for managing client work.</p>
          <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>See Simplifyte in Action</Link>
        </div>
      </section>

      <section style={{ padding: "60px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "26px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>Why WhatsApp fails as a workflow tool</h2>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "16px" }}>
            Most Indian CA firms and professional service teams run a significant portion of their workflow on WhatsApp — work assignments, document requests, review approvals, client reminders. It works at small scale. As the firm grows, it becomes a reliability and accountability problem: messages get missed, instructions get lost, nothing is traceable.
          </p>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "32px" }}>
            Simplifyte gives every piece of work a structured home — with status, ownership, deadlines, documents, review, and audit trail built in. No more "can you check the chat from last Tuesday."
          </p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#093A5B", color: "#fff" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>WhatsApp</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Simplifyte</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#f8fafc" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 500, color: "#1e293b" }}>{row.feature}</td>
                    <td style={{ padding: "12px 16px", color: "#ef4444" }}>✗ {row.whatsapp}</td>
                    <td style={{ padding: "12px 16px", color: "#16a34a" }}>✓ {row.simplifyte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={{ padding: "40px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>Signs your firm has outgrown WhatsApp for workflow</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "16px" }}>
            {[
              { title: "Work gets missed", desc: "A task assigned in a group message doesn't get a reply. No one knows if it was picked up until the partner asks." },
              { title: "Documents arrive late", desc: "Document requests are sent, replied to, and then the attachment is buried in the chat three days later." },
              { title: "No accountability", desc: "When something goes wrong, there's no record of who was responsible for each step — just a scrollable chat." },
              { title: "Partner is constantly interrupted", desc: "Instead of a dashboard, the partner asks individual team members for status on each client's work." },
            ].map((item, i) => (
              <div key={i} style={{ background: "#fff", padding: "18px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{item.title}</h3>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px", textAlign: "center", background: "#093A5B", color: "#fff" }}>
        <h2 style={{ fontSize: "26px", fontWeight: 700, marginBottom: "14px" }}>Replace WhatsApp chaos with structured workflow</h2>
        <p style={{ opacity: 0.8, marginBottom: "24px", fontSize: "16px" }}>Book a 20-minute demo and see what Simplifyte looks like for your firm.</p>
        <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "13px 30px", borderRadius: "8px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>Book a Demo <ArrowRight size={16} /></Link>
      </section>
    </main>
    <CustomFooter />
  </>
);

export default WhatsAppVsSimplifyte;
